import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ButtonComponent, SelectComponent } from '@shared/components';
import { CountriesService, LanguageService, SidebarOverlayService } from '@shared/services';
import { Languages, MenuItem, ROUTES } from '@shared/models';

import { NavigationService } from '../../../services';
import { MenuBurgerComponent } from '../menu-burger/menu-burger.component';

enum LanguageLabel {
  English = 'English',
  Russian = 'Russian',
}

@Component({
  selector: 'rp-home-navigation-mobile',
  standalone: true,
  templateUrl: './navigation-mobile.component.html',
  styleUrl: './navigation-mobile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    SelectComponent,
    ReactiveFormsModule,
    ButtonComponent,
    MenuBurgerComponent,
  ],
  animations: [
    trigger('animation', [
      state('in', style({ transform: 'translateY(0)' })),
      state('out', style({ transform: 'translateY(-100%)' })),
      transition('void => in', [
        style({ transform: 'translateY(-100%)' }),
        animate('0.6s ease', style({ transform: 'translateY(0)' })),
      ]),
      transition('in => out', [animate('0.6s ease', style({ transform: 'translateY(-100%)' }))]),
    ]),
  ],
})
export class NavigationMobileComponent {
  readonly languages: LanguageLabel[] = Object.values(LanguageLabel);

  languageControl: FormControl<LanguageLabel>;
  animationState: 'in' | 'out' = 'in';

  private readonly _sideBarOverlayService = inject(SidebarOverlayService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _languageService = inject(LanguageService);
  private readonly _countriesService = inject(CountriesService);
  private readonly _translateService = inject(TranslateService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _router = inject(Router);

  constructor() {
    this._setLang();

    this.languageControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((language: LanguageLabel) => {
        const lang = language === LanguageLabel.English ? Languages.EN : Languages.RU;
        this._languageService.setLanguage(lang);
        this._countriesService.registerLocale(lang);
      });
  }

  onClose(): void {
    this.animationState = 'out';

    setTimeout(() => {
      this._sideBarOverlayService.close();
    }, 300);
  }

  onNavigateToItem(item: MenuItem): void {
    this.onClose();
    this._navigationService.navigateToItem(item.sectionId, true);
  }

  navigateToSignUp(): void {
    this._router.navigate([ROUTES.signUp]);
    this.onClose();
  }

  navigateToSignIn(): void {
    this._router.navigate([ROUTES.auth, ROUTES.signIn]);
    this.onClose();
  }

  private _setLang(): void {
    const currentLanguage = this._languageService.getCurrentLanguage();
    this.languageControl = new FormControl<LanguageLabel>(this._parsedLanguage(currentLanguage));
    this._translateService.use(currentLanguage);
  }

  private _parsedLanguage(lang: Languages): LanguageLabel {
    return lang === Languages.EN ? LanguageLabel.English : LanguageLabel.Russian;
  }
}
